import { useId, type ReactNode } from 'react';
import usePrefersReducedMotion from '../../../hooks/usePrefersReducedMotion';
import type { BirdIconDef, BirdIconMotion, BirdIconProps } from './types';

type BirdIconSvgProps = BirdIconProps & {
  def: BirdIconDef;
};

function MotionWrap({
  motion,
  animated,
  children,
}: {
  motion: BirdIconMotion;
  animated: boolean;
  children: ReactNode;
}) {
  if (!animated || motion === 'none') {
    return <g>{children}</g>;
  }

  switch (motion) {
    case 'bob':
      return (
        <g>
          {children}
          <animateTransform
            attributeName="transform"
            type="translate"
            values="0 0; 0 -1.2; 0 0"
            dur="2.8s"
            repeatCount="indefinite"
          />
        </g>
      );
    case 'pulse':
      return (
        <g>
          {children}
          <animate attributeName="opacity" values="0.65;1;0.65" dur="2.4s" repeatCount="indefinite" />
        </g>
      );
    case 'peck':
      return (
        <g>
          {children}
          <animateTransform
            attributeName="transform"
            type="rotate"
            values="0 12 14; 10 12 14; 0 12 14"
            dur="1.6s"
            repeatCount="indefinite"
          />
        </g>
      );
    case 'flutter':
      return (
        <g>
          {children}
          <animateTransform
            attributeName="transform"
            type="rotate"
            values="0 12 12; -12 12 12; 8 12 12; 0 12 12"
            dur="1.1s"
            repeatCount="indefinite"
          />
        </g>
      );
    case 'wing':
    default:
      return (
        <g>
          {children}
          <animateTransform
            attributeName="transform"
            type="rotate"
            values="0 12 12; -9 12 12; 0 12 12"
            dur="2.2s"
            repeatCount="indefinite"
          />
        </g>
      );
  }
}

export default function BirdIconSvg({
  def,
  className = 'h-6 w-6',
  title,
  animated = true,
}: BirdIconSvgProps) {
  const gradientId = useId().replace(/:/g, '');
  const reducedMotion = usePrefersReducedMotion();
  const motionOn = animated && !reducedMotion;
  const motion = def.motion ?? 'wing';
  const stroke = def.solid ? undefined : 'currentColor';
  const fill = def.solid ? 'currentColor' : 'none';

  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      role="img"
      aria-hidden={title ? undefined : true}
      aria-label={title}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.85" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="1" />
        </linearGradient>
      </defs>
      <MotionWrap motion={motion} animated={motionOn}>
        <g
          fill={fill}
          stroke={stroke}
          strokeWidth={def.solid ? 0 : 1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
          dangerouslySetInnerHTML={{ __html: def.markup }}
        />
      </MotionWrap>
    </svg>
  );
}
